// src/lib/carPricesApi.ts
import { supabase } from './supabase';

// Helper function to check if a string is a UUID
function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Helper to generate slugs
export const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Split accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-'); // Replace multiple - with single -
};

export interface CarBrand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CarModel {
  id: string;
  brand_id: string;
  name: string;
  slug: string;
  description: string | null;
  starting_price: number | null;
  engine: string | null;
  transmission: string | null;
  power: string | null;
  fuel_type: string | null;
  carrosserie: string | null;
  nombre_de_places: string | null;
  images: string[] | null;
  features: string[] | null;
  specifications: Record<string, any> | null;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export interface CarModelVersion {
  id: string;
  model_id: string;
  name: string;
  slug: string;
  description: string | null;
  price: number | null;
  engine: string | null;
  transmission: string | null;
  power: string | null;
  fuel_type: string | null;
  carrosserie: string | null;
  nombre_de_places: string | null;
  images: string[] | null;
  features: string[] | null;
  specifications: Record<string, any> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CarModelWithBrand extends CarModel {
  brand: CarBrand;
}

// Helper function to get the session token
function getSessionToken(): string | null {
  if (typeof window === 'undefined') return null;
  
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'admin_session_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

export const carPricesApi = {
  // Brands
  async getBrands(): Promise<CarBrand[]> {
    try {
      const { data, error } = await supabase
        .from('car_brands')
        .select('*')
        .order('name'); // Removed is_active filter to see all in admin
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error in getBrands:', error);
      return [];
    }
  },

  async getBrand(identifier: string): Promise<CarBrand | null> {
    try {
      let query = supabase.from('car_brands').select('*');
      
      if (isUUID(identifier)) {
        query = query.eq('id', identifier);
      } else {
        query = query.eq('slug', identifier);
      }
      
      const { data, error } = await query.single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error in getBrand:', error);
      return null;
    }
  },

  // Admin mutations must validate our custom admin session server-side.
  // The browser session cookie is NOT a Supabase Auth JWT.
  async manageBrand(action: 'create' | 'update' | 'delete', brandId: string | null, payload: Record<string, unknown>): Promise<CarBrand> {
    // AuthContext uses session_token; admin_session_token is a legacy name.
    const token = document.cookie.split(';').map(c => c.trim())
      .find(c => c.startsWith('session_token='))?.slice('session_token='.length);
    if (!token) throw new Error('Session administrateur absente. Veuillez vous reconnecter.');
    const { data, error } = await supabase.rpc('admin_manage_car_brand', {
      p_session_token: decodeURIComponent(token),
      p_action: action,
      p_brand_id: brandId,
      p_payload: payload
    });
    if (error) throw error;
    return data as CarBrand;
  },

  async createBrand(brand: Omit<CarBrand, 'id' | 'created_at' | 'updated_at' | 'slug'>): Promise<CarBrand> {
    return this.manageBrand('create', null, { ...brand, slug: slugify(brand.name) });
  },

  async updateBrand(id: string, brand: Partial<CarBrand>): Promise<CarBrand> {
    return this.manageBrand('update', id, {
      ...brand,
      ...(brand.name ? { slug: slugify(brand.name) } : {})
    });
  },

  async deleteBrand(id: string): Promise<void> {
    await this.manageBrand('delete', id, {});
  },

  // Models
  async getModels(brandId?: string): Promise<CarModelWithBrand[]> {
    try {
      let query = supabase
        .from('car_models')
        .select('*, car_brands(*)');
        // Removed .eq('is_active', true) to show everything in admin
      
      if (brandId) {
        if (isUUID(brandId)) {
          query = query.eq('brand_id', brandId);
        } else {
          const brand = await this.getBrand(brandId);
          if (brand) {
            query = query.eq('brand_id', brand.id);
          }
        }
      }
      
      const { data, error } = await query.order('name');
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error in getModels:', error);
      return [];
    }
  },

  async getModel(identifier: string): Promise<CarModelWithBrand | null> {
    try {
      let query = supabase
        .from('car_models')
        .select('*, car_brands(*)');
      
      if (isUUID(identifier)) {
        query = query.eq('id', identifier);
      } else {
        query = query.eq('slug', identifier);
      }
      
      const { data, error } = await query.single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error in getModel:', error);
      return null;
    }
  },

  // Use the existing custom admin session, validated by the database RPC.
  async manageModel(action: 'create' | 'update' | 'delete', modelId: string | null, payload: Record<string, unknown>): Promise<CarModel> {
    const token = document.cookie.split(';').map(c => c.trim())
      .find(c => c.startsWith('session_token='))?.slice('session_token='.length);
    if (!token) throw new Error('Session administrateur absente. Veuillez vous reconnecter.');
    const { data, error } = await supabase.rpc('admin_manage_car_model', {
      p_session_token: decodeURIComponent(token),
      p_action: action,
      p_model_id: modelId,
      p_payload: payload
    });
    if (error) throw error;
    return data as CarModel;
  },

  async createModel(model: Omit<CarModel, 'id' | 'created_at' | 'updated_at' | 'slug'>): Promise<CarModel> {
    return this.manageModel('create', null, { ...model, slug: slugify(model.name) });
  },

  async updateModel(id: string, model: Partial<CarModel>): Promise<CarModel> {
    return this.manageModel('update', id, {
      ...model,
      ...(model.name ? { slug: slugify(model.name) } : {})
    });
  },

  async deleteModel(id: string): Promise<void> {
    await this.manageModel('delete', id, {});
  },

  // Model Versions
  async getModelVersions(modelId: string): Promise<CarModelVersion[]> {
    try {
      let query = supabase
        .from('car_model_versions')
        .select('*');
        // Removed is_active filter for admin view
      
      if (isUUID(modelId)) {
        query = query.eq('model_id', modelId);
      } else {
        const model = await this.getModel(modelId);
        if (model) {
          query = query.eq('model_id', model.id);
        }
      }
      
      const { data, error } = await query.order('name');
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error in getModelVersions:', error);
      return [];
    }
  },

  async getModelVersion(identifier: string): Promise<CarModelVersion | null> {
    try {
      let query = supabase
        .from('car_model_versions')
        .select('*');
      
      if (isUUID(identifier)) {
        query = query.eq('id', identifier);
      } else {
        query = query.eq('slug', identifier);
      }
      
      const { data, error } = await query.single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error in getModelVersion:', error);
      return null;
    }
  },

  // FIX: Omit slug from input, generate it inside
  async createModelVersion(version: Omit<CarModelVersion, 'id' | 'created_at' | 'updated_at' | 'slug'>): Promise<CarModelVersion> {
    try {
      const slug = slugify(version.name);

      const { data, error } = await supabase
        .from('car_model_versions')
        .insert([{ ...version, slug }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error in createModelVersion:', error);
      throw error;
    }
  },

  async updateModelVersion(id: string, version: Partial<CarModelVersion>): Promise<CarModelVersion> {
    try {
      const updates: any = { ...version, updated_at: new Date().toISOString() };
      
      if (version.name) {
        updates.slug = slugify(version.name);
      }

      const { data, error } = await supabase
        .from('car_model_versions')
        .update(updates)
        .eq('id', id)
        .select(); // Removed .single() to fix PGRST116 on deactivate
      
      if (error) throw error;
      return (data && data.length > 0) ? data[0] : ({ id, ...version } as CarModelVersion);
    } catch (error) {
      console.error('Error in updateModelVersion:', error);
      throw error;
    }
  },

  async deleteModelVersion(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('car_model_versions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error in deleteModelVersion:', error);
      throw error;
    }
  },

  async getVersion(identifier: string): Promise<CarModelVersion | null> {
    return this.getModelVersion(identifier);
  },
};